/**
 * Feedback triage — where a comment becomes a commitment.
 *
 * Every useful submission acquires an owner, a disposition and an outcome.
 * The queue shows what is open; opening an item shows what the person said,
 * where they were, and the item's whole story so far; the action bar offers
 * only the moves the state machine allows from here. The server enforces the
 * same rules, so a stale screen gets a 409 with the reason rather than a
 * silent overwrite.
 *
 * The note on Park, Ship and Close is shown to the person who reported it.
 * It is written to them.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquare, Loader2, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  feedbackApi,
  CATEGORIES, CATEGORY_LABEL, EXTERNAL_TOOL_LABEL, STATUS_LABEL, WORKSPACE_LABEL,
  Category, FeedbackStatus, Owner, QueueCounts, QueueDetail, QueueItem, TriageAction, Workspace,
} from '../../../services/feedbackApi';

type Filter = 'open' | 'parked' | 'done' | 'all';

/** Mirrors the server's ACTIONS table; the server is the authority. */
const ALLOWED: Record<FeedbackStatus, TriageAction[]> = {
  NEW: ['TRIAGE', 'ASSIGN_OWNER', 'REQUEST_EVIDENCE', 'PARK', 'CLOSE'],
  TRIAGED: ['ACCEPT', 'ASSIGN_OWNER', 'REQUEST_EVIDENCE', 'PARK', 'CLOSE'],
  NEEDS_EVIDENCE: ['TRIAGE', 'ACCEPT', 'ASSIGN_OWNER', 'PARK', 'CLOSE'],
  ACCEPTED: ['SHIP', 'LINK_IMPLEMENTATION', 'ASSIGN_OWNER', 'REQUEST_EVIDENCE', 'PARK', 'CLOSE'],
  PARKED: ['ACCEPT', 'ASSIGN_OWNER', 'CLOSE'],
  SHIPPED: [],
  CLOSED: [],
};

const ACTION_LABEL: Record<TriageAction, string> = {
  TRIAGE: 'Triage',
  ASSIGN_OWNER: 'Assign owner',
  REQUEST_EVIDENCE: 'Ask for more detail',
  ACCEPT: 'Accept',
  PARK: 'Park',
  LINK_IMPLEMENTATION: 'Link the change',
  SHIP: 'Mark shipped',
  CLOSE: 'Close',
};

/** Which fields an action shows, and which it requires. */
const FIELDS: Record<TriageAction, { category?: boolean; owner?: boolean; note?: 'required' | 'optional'; release?: boolean; link?: boolean; noteToReporter?: boolean }> = {
  TRIAGE: { category: true, owner: true, note: 'optional' },
  ASSIGN_OWNER: { owner: true },
  REQUEST_EVIDENCE: { note: 'required' },
  ACCEPT: { owner: true, release: true, note: 'optional' },
  PARK: { note: 'required', noteToReporter: true },
  LINK_IMPLEMENTATION: { link: true },
  SHIP: { note: 'required', release: true, noteToReporter: true },
  CLOSE: { note: 'required', noteToReporter: true },
};

const EVENT_LABEL: Record<string, string> = {
  SUBMITTED: 'Submitted',
  TRIAGED: 'Triaged',
  OWNER_ASSIGNED: 'Owner assigned',
  EVIDENCE_REQUESTED: 'More detail requested',
  ACCEPTED: 'Accepted',
  PARKED: 'Parked',
  IMPLEMENTATION_LINKED: 'Change linked',
  SHIPPED: 'Shipped',
  CLOSED: 'Closed',
};

const STATUS_TONE: Record<FeedbackStatus, string> = {
  NEW: 'text-rose-700 bg-rose-50 border-rose-200',
  TRIAGED: 'text-gray-700 bg-gray-50 border-gray-200',
  NEEDS_EVIDENCE: 'text-amber-800 bg-amber-50 border-amber-200',
  ACCEPTED: 'text-emerald-800 bg-emerald-50 border-emerald-200',
  PARKED: 'text-gray-600 bg-gray-50 border-gray-200',
  SHIPPED: 'text-emerald-800 bg-emerald-50 border-emerald-200',
  CLOSED: 'text-gray-500 bg-gray-50 border-gray-200',
};

const fmtWhen = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The SharePoint export: the same eight fields, plus disposition. */
function toCsv(items: QueueItem[]): string {
  const cols: Array<[string, (r: QueueItem) => unknown]> = [
    ['received', (r) => r.created_at],
    ['status', (r) => STATUS_LABEL[r.status]],
    ['workspace', (r) => WORKSPACE_LABEL[r.workspace]],
    ['view', (r) => r.view_name],
    ['from', (r) => r.submitted_by_name],
    ['facility', (r) => r.facility_name],
    ['what they were trying to do', (r) => r.task_attempted],
    ['what happened', (r) => r.observed_friction],
    ['what would have made it easier', (r) => r.desired_outcome],
    ['left eMerge', (r) => (r.left_platform ? 'yes' : 'no')],
    ['where', (r) => (r.external_tool ? EXTERNAL_TOOL_LABEL[r.external_tool] : '')],
    ['category', (r) => (r.category ? CATEGORY_LABEL[r.category] : '')],
    ['owner', (r) => r.owner_name],
    ['resolution', (r) => r.resolution_note],
    ['target release', (r) => r.target_release],
    ['device', (r) => r.device_class],
    ['build', (r) => r.build_sha],
    ['environment', (r) => r.environment],
  ];
  const head = cols.map(([h]) => csvCell(h)).join(',');
  const rows = items.map((r) => cols.map(([, f]) => csvCell(f(r))).join(','));
  return [head, ...rows].join('\n');
}

export default function FeedbackTriage() {
  const [filter, setFilter] = useState<Filter>('open');
  const [workspace, setWorkspace] = useState<Workspace | ''>('');
  const [items, setItems] = useState<QueueItem[]>([]);
  const [counts, setCounts] = useState<QueueCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [owners, setOwners] = useState<Owner[]>([]);

  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<QueueDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [action, setAction] = useState<TriageAction | ''>('');
  const [note, setNote] = useState('');
  const [category, setCategory] = useState<Category | ''>('');
  const [owner, setOwner] = useState('');
  const [release, setRelease] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await feedbackApi.queue(filter, workspace);
      if (res.success && res.data) {
        setItems(res.data.items);
        setCounts(res.data.counts);
      } else {
        setLoadError(res.error || res.message || 'The queue could not be loaded.');
      }
    } catch {
      setLoadError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [filter, workspace]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    feedbackApi.owners().then((res) => { if (res.success && res.data) setOwners(res.data); }).catch(() => {});
  }, []);

  const openDetail = useCallback(async (id: string) => {
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    setAction('');
    setNote(''); setCategory(''); setOwner(''); setRelease(''); setLink('');
    try {
      const res = await feedbackApi.get(id);
      if (res.success && res.data) setDetail(res.data);
      else toast.error(res.error || 'Could not open this item.');
    } catch {
      toast.error('Could not reach the server.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const toggle = (id: string) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    openDetail(id);
  };

  const apply = async () => {
    if (!detail || !action) return;
    const f = FIELDS[action];
    if (f.category && !category) { toast.error('Choose a category.'); return; }
    if (f.note === 'required' && !note.trim()) { toast.error(f.noteToReporter ? 'Write the note the reporter will read.' : 'Write a note.'); return; }
    if (f.link && !/^https:\/\//i.test(link.trim())) { toast.error('Paste an https link.'); return; }
    if (action === 'ASSIGN_OWNER' && !owner) { toast.error('Choose an owner.'); return; }
    setBusy(true);
    try {
      const res = await feedbackApi.act(detail.id, {
        action,
        note: note.trim() || undefined,
        category: category || undefined,
        owner_user_id: owner || undefined,
        target_release: release.trim() || undefined,
        link: link.trim() || undefined,
      });
      if (res.success && res.data) {
        toast.success(`${ACTION_LABEL[action]}: now ${STATUS_LABEL[res.data.status]}`);
        await Promise.all([load(), openDetail(detail.id)]);
      } else {
        toast.error(res.error || res.message || 'The server refused this move.');
      }
    } catch {
      toast.error('Could not reach the server. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(items)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emerge-feedback-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const allowed = useMemo(() => (detail ? ALLOWED[detail.status] : []), [detail]);
  const fields = action ? FIELDS[action] : null;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-emerald-600" />
            Feedback
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            What people told us from inside eMerge, where the friction happened. Every useful
            item gets an owner and an outcome, and the person who wrote it sees that outcome in
            their own workspace. The note on Park, Ship and Close is written to them.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={items.length === 0}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            ['New', counts.new, counts.new ? 'text-rose-700' : 'text-gray-900'],
            ['Under review', counts.triaged, 'text-gray-900'],
            ['Needs detail', counts.needs_evidence, 'text-gray-900'],
            ['Accepted', counts.accepted, 'text-gray-900'],
            ['Left eMerge to finish', counts.left_platform, 'text-gray-900'],
          ].map(([label, n, tone]) => (
            <div key={String(label)} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className={`text-2xl font-semibold tabular-nums ${tone}`}>{n}</div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([
          ['open', 'Open'],
          ['parked', 'Parked'],
          ['done', 'Shipped & closed'],
          ['all', 'Everything'],
        ] as Array<[Filter, string]>).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={`text-sm px-3 py-1 rounded border ${filter === key
              ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {label}
          </button>
        ))}
        <select
          value={workspace}
          onChange={(e) => setWorkspace(e.target.value as Workspace | '')}
          aria-label="Filter by workspace"
          className="ml-auto text-sm border border-gray-300 rounded px-2 py-1 bg-white"
        >
          <option value="">Every workspace</option>
          {(Object.keys(WORKSPACE_LABEL) as Workspace[]).map((w) => <option key={w} value={w}>{WORKSPACE_LABEL[w]}</option>)}
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && loadError && (
        <div className="text-center py-12 text-rose-700 border border-rose-200 bg-rose-50 rounded-lg">
          {loadError}
          <div className="mt-3">
            <button type="button" onClick={load} className="text-sm px-3 py-1 rounded border border-rose-300 bg-white text-rose-800">Try again</button>
          </div>
        </div>
      )}

      {!loading && !loadError && items.length === 0 && (
        <div className="text-center py-16 text-gray-500 border border-dashed border-gray-300 rounded-lg">
          {filter === 'open' ? 'Nothing open. Everything sent has an outcome.' : 'Nothing here.'}
        </div>
      )}

      {!loading && !loadError && items.length > 0 && (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Received</th>
                <th className="text-left px-3 py-2 font-medium">From</th>
                <th className="text-left px-3 py-2 font-medium">Trying to</th>
                <th className="text-left px-3 py-2 font-medium">Left eMerge</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Owner</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((row) => {
                const open = openId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr className={open ? 'bg-emerald-50/40' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-2.5 text-gray-700 tabular-nums whitespace-nowrap">{fmtWhen(row.created_at)}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-gray-900">{row.submitted_by_name}</div>
                        <div className="text-xs text-gray-500">{WORKSPACE_LABEL[row.workspace]} · {row.view_name}{row.facility_name ? ` · ${row.facility_name}` : ''}</div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-800 max-w-md">
                        <div className="line-clamp-2">{row.task_attempted}</div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                        {row.left_platform ? `Yes · ${row.external_tool ? EXTERNAL_TOOL_LABEL[row.external_tool] : ''}` : 'No'}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded border ${STATUS_TONE[row.status]}`}>{STATUS_LABEL[row.status]}</span>
                        {row.category && <div className="text-xs text-gray-500 mt-1">{CATEGORY_LABEL[row.category]}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{row.owner_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-2 py-2.5">
                        <button
                          type="button"
                          onClick={() => toggle(row.id)}
                          aria-expanded={open}
                          aria-label={open ? 'Collapse item' : 'Open item'}
                          className="p-1 rounded hover:bg-gray-100 text-gray-500"
                        >
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>

                    {open && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={7} className="px-4 py-4">
                          {detailLoading && (
                            <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                          )}
                          {detail && detail.id === row.id && (
                            <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                              <div className="space-y-4">
                                <dl className="space-y-3 text-sm">
                                  <div>
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">What they were trying to do</dt>
                                    <dd className="text-gray-900 whitespace-pre-wrap">{detail.task_attempted}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">What happened</dt>
                                    <dd className="text-gray-900 whitespace-pre-wrap">{detail.observed_friction}</dd>
                                  </div>
                                  <div>
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">What would have made it easier</dt>
                                    <dd className="text-gray-900 whitespace-pre-wrap">{detail.desired_outcome || <span className="text-gray-400">Not said</span>}</dd>
                                  </div>
                                </dl>
                                <div className="text-xs text-gray-500 border-t border-gray-200 pt-3 flex flex-wrap gap-x-4 gap-y-1">
                                  <span>{WORKSPACE_LABEL[detail.workspace]} · {detail.route_template} · {detail.view_name}</span>
                                  {detail.facility_name && <span>{detail.facility_name}</span>}
                                  <span>{detail.device_class ? detail.device_class.toLowerCase() : 'device unknown'}{detail.viewport_width ? ` · ${detail.viewport_width}px` : ''}</span>
                                  <span>build {detail.build_sha || 'unknown'} · {detail.environment}</span>
                                  <span>{detail.prompt_source === 'WORKFLOW_PROMPT' ? 'after the workflow prompt' : 'from the Feedback door'}</span>
                                  {detail.roles_held.length > 0 && <span>roles: {detail.roles_held.join(', ')}</span>}
                                  <span>{detail.submitted_by_email}</span>
                                </div>

                                <div>
                                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Story so far</div>
                                  <ol className="space-y-1.5 text-sm">
                                    {detail.events.map((ev) => (
                                      <li key={ev.id} className="flex gap-3">
                                        <span className="text-gray-500 tabular-nums whitespace-nowrap w-28 shrink-0">{fmtWhen(ev.created_at)}</span>
                                        <span className="text-gray-900">
                                          <strong className="font-medium">{EVENT_LABEL[ev.event_type] || ev.event_type}</strong>
                                          {ev.actor_name && <span className="text-gray-500"> · {ev.actor_name}</span>}
                                          {ev.note && <div className="text-gray-700 whitespace-pre-wrap">{ev.note}</div>}
                                          {typeof ev.detail?.link === 'string' && (
                                            <div><a className="text-emerald-700 underline" href={ev.detail.link} target="_blank" rel="noopener noreferrer">{ev.detail.link}</a></div>
                                          )}
                                        </span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              </div>

                              <div className="border border-gray-200 rounded-lg bg-white p-4 space-y-3 self-start">
                                <div className="text-xs uppercase tracking-wide text-gray-500">Next move</div>
                                {allowed.length === 0 && (
                                  <p className="text-sm text-gray-600">
                                    {detail.status === 'SHIPPED' ? 'Shipped.' : 'Closed.'} {detail.resolution_note}
                                  </p>
                                )}
                                {allowed.length > 0 && (
                                  <>
                                    <div className="flex flex-wrap gap-1.5">
                                      {allowed.map((a) => (
                                        <button
                                          key={a}
                                          type="button"
                                          onClick={() => setAction(a)}
                                          aria-pressed={action === a}
                                          className={`text-xs px-2.5 py-1 rounded border ${action === a
                                            ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                        >
                                          {ACTION_LABEL[a]}
                                        </button>
                                      ))}
                                    </div>

                                    {fields && (
                                      <div className="space-y-2.5">
                                        {fields.category && (
                                          <label className="block text-sm">
                                            <span className="text-gray-700">Category</span>
                                            <select value={category} onChange={(e) => setCategory(e.target.value as Category | '')} className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                                              <option value="">Choose…</option>
                                              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                                            </select>
                                          </label>
                                        )}
                                        {fields.owner && (
                                          <label className="block text-sm">
                                            <span className="text-gray-700">Owner{detail.owner_name ? ` (now ${detail.owner_name})` : ''}</span>
                                            <select value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                                              <option value="">{detail.owner_name ? 'Keep' : 'Choose…'}</option>
                                              {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                                            </select>
                                          </label>
                                        )}
                                        {fields.release && (
                                          <label className="block text-sm">
                                            <span className="text-gray-700">Target release <span className="text-gray-400">optional</span></span>
                                            <input value={release} onChange={(e) => setRelease(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1" placeholder="e.g. Sep 5" />
                                          </label>
                                        )}
                                        {fields.link && (
                                          <label className="block text-sm">
                                            <span className="text-gray-700">Link to the change</span>
                                            <input value={link} onChange={(e) => setLink(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1" placeholder="https://…" />
                                          </label>
                                        )}
                                        {fields.note && (
                                          <label className="block text-sm">
                                            <span className="text-gray-700">
                                              {fields.noteToReporter ? 'Note to the person who reported it' : 'Note'}
                                              {fields.note === 'optional' && <span className="text-gray-400"> optional</span>}
                                            </span>
                                            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1" />
                                            {fields.noteToReporter && <span className="text-xs text-gray-500">They will read this in their workspace. No jargon, no ticket numbers.</span>}
                                          </label>
                                        )}
                                        <button
                                          type="button"
                                          onClick={apply}
                                          disabled={busy}
                                          className="text-sm px-3 py-1.5 rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50"
                                        >
                                          {busy ? 'Applying…' : ACTION_LABEL[action as TriageAction]}
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
